import {
  FDBodyText,
  FDIcon,
  FDPaper,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import React from 'react';
import { dataAttrs } from 'Helpers/Testing';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMobileLayout from 'Hooks/useMobileLayout';
import { navigateTo } from 'Helpers/Redirect';
import { dashboardWithModal } from 'Consts/clientUris';

export const RoadmapExecutiveSummaryQuestions = ({ isRoadmapDeliverable = false }) => {
  const { resource } = useMetaLayoutContext();
  const [loading, setLoading] = React.useState(false);
  const isMobile = useMobileLayout();

  const { onAgentReview } = useSelectScenarioContext();
  const { loadPageContentLink } = usePageContentContext();
  const { isAgent } = useUserContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const scheduleMeetingRel = resource.withRelationship(
    fr.relationships.scheduleMeeting.name,
    (rel) => rel,
    () => null,
  );

  const reviewScenarioRel = resource.withRelationship(
    fr.relationships.reviewScenario.name,
    (rel) => rel,
    () => null,
  );

  const onScheduleMeetingClick = async () => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Click Book a meeting');
    navigateTo(
      dashboardWithModal('clientSchedulerModal', {
        link: scheduleMeetingRel.link('related').url(),
      }),
    );
  };

  const onReviewScenarioClick = async () => {
    setLoading(true);
    // This review section is displayed to both the client within page context and to the agent not within page context.
    // Therefore, we need to pass this function as a prop so we can change which component we show to the agent.
    if (isAgent) {
      await onAgentReview(resource);
      return;
    }

    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Click Review on Scenario', { eventProperties: { title: resource.attr('title') } });
    await loadPageContentLink(reviewScenarioRel.link('related'));
    // We intentionally do not set loading to false because this operation will navigate us away from the page
    // and we should not attempt to update state on an unmounted component
  };

  if (!scheduleMeetingRel && !reviewScenarioRel) {
    return null; // No actions available
  }

  const buttons = (
    <FDStack direction={'row'} spacing={isMobile ? 2 : 3} alignItems={'center'} wrap>
      {scheduleMeetingRel ? (
        <FDSecondaryActionButton
          label={scheduleMeetingRel.meta('prompt')}
          onClick={onScheduleMeetingClick}
          loading={loading}
          {...dataAttrs('schedule-meeting-button', resource)}
        />
      ) : null}
      {scheduleMeetingRel && reviewScenarioRel ? (
        <FDBodyText value={'or'} />
      ) : null}
      {reviewScenarioRel ? (
        <FDSecondaryActionButton
          label={reviewScenarioRel.meta('prompt')}
          onClick={onReviewScenarioClick}
          loading={loading}
          {...dataAttrs('review-scenario-button', resource)}
        />
      ) : null}
    </FDStack>
  );

  const hasTitleInfo = !!resource.attr('title') || !!resource.attr('subtitle');
  return (
    <FDPaper appearance={'flat'} color={'subtle'} padding={600} {...dataAttrs('roadmap-executive-summary-questions-section')} width={isMobile || isRoadmapDeliverable ? '100%' : '90%'}>
      {hasTitleInfo ? (
        <FDStack direction={'column'} spacing={4}>
          <FDStack direction={'column'} spacing={2}>
            <FDStack direction={'row'} spacing={2}>
              <FDIcon icon={'help'} color={'primary'} />
              <MetaControlField attr={'title'} />
            </FDStack>
            <MetaControlField attr={'subtitle'} />
          </FDStack>
          {buttons}
        </FDStack>
      ) : buttons}
    </FDPaper>
  );
};
